import classNames from "classnames";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";

import useCheckDevice from "@repo/shopifyV2/hooks/useCheckDevice";
import useOrderInfo from "@repo/shopifyV2/hooks/useOrderInfo";
import useOrderStatus from "@repo/shopifyV2/hooks/useOrderStatus";
import type { IOrdersEdgesNode } from "@repo/shopifyV2/interface/customer";
import { OrderStatusButtonType } from "@repo/shopifyV2/types/order";
import { extractId } from "@repo/shopifyV2/utils/common";
import { OrderStatusTrans, getTrackingInfo } from "@repo/shopifyV2/utils/order";

import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Price from "@atoms/Price";

import OrderStatusButton from "@organisms/OrderStatusButton";

import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

export default function OrderHistoryCard({ node }: { node: IOrdersEdgesNode }) {
  const { t } = useTranslation();
  const { isPC } = useCheckDevice();
  const { lang } = useLanguage();

  const { activeButton, orderStatus } = useOrderStatus(node, lang === "ko");

  // 주문 정보
  const { totalAmount } = useOrderInfo(node);

  // 배송 정보
  const trackingInfo = getTrackingInfo({ order: node, lang });

  return (
    <li className="pc:col-span-10 pc:grid-cols-subgrid pc:gap-y-0 col-span-8 grid grid-cols-subgrid gap-y-4 border-b pb-10">
      <div className="pc:col-span-1 col-span-3 col-start-1 flex flex-col gap-1.5">
        <Link as="wrapper" className="pc:gap-1.5 flex! flex-col gap-2" href={`${URLS.ORDER}${extractId(node.id)}`}>
          <div className="text-md flex flex-col gap-0.5 leading-none">
            <span>{node.name}</span>
            <span>{dayjs(node.processedAt).format("MMM DD, YYYY")}</span>
          </div>
          <span className="text-md leading-none">{orderStatus ? t(OrderStatusTrans[orderStatus]) : orderStatus}</span>
        </Link>
        {trackingInfo ? (
          <Link as="text" className="text-md w-fit leading-none font-normal underline" href={trackingInfo?.url}>
            {trackingInfo.number}
          </Link>
        ) : null}
      </div>
      {node?.lineItems.edges.length > 0 ? (
        <Link
          as="wrapper"
          className="pc:col-span-3 pc:col-start-2 pc:row-start-1 col-span-8 col-start-1 row-start-2 flex flex-col"
          href={`${URLS.ORDER}${extractId(node.id)}`}
        >
          <ol className="text-md flex flex-col gap-1 leading-none">
            {node?.lineItems?.edges.map((item, index) => (
              <li key={`${node.id}-${index}-title`}>{item.node.title}</li>
            ))}
          </ol>
        </Link>
      ) : null}
      {isPC ? (
        <Link as="wrapper" className="pc:col-span-1 col-span-3 block" href={`${URLS.ORDER}${extractId(node.id)}`}>
          <Price className="text-md leading-none" price={totalAmount} />
        </Link>
      ) : null}
      <Link
        as="wrapper"
        className={classNames(
          "pc:col-start-6 pc:row-start-1 col-span-8 row-start-3 block",
          lang === "ko" ? "pc:col-span-4" : "pc:col-span-3",
        )}
        href={`${URLS.ORDER}${extractId(node.id)}`}
      >
        <ol className="flex flex-wrap gap-0.5">
          {node?.lineItems?.edges.map((item, index) => (
            <li key={`${node.id}-${index}-thumbnail`}>
              <Image
                className="pc:w-20 pc:max-w-20 w-15 max-w-15"
                height={item.node.variant?.image?.height || 1707}
                src={item.node.variant?.image?.url || node.featuredImage?.value || ""}
                width={item.node.variant?.image?.width || 1280}
              />
            </li>
          ))}
        </ol>
      </Link>
      <div
        className={classNames(
          "pc:gap-0 pc:flex-row pc:justify-end pc:items-start col-span-4 col-start-5 flex flex-col items-end gap-2",
          lang === "ko" ? "pc:col-span-1 pc:col-start-11" : "pc:col-span-2 pc:col-start-9",
        )}
      >
        {!isPC && <Price className="text-md leading-none" price={totalAmount} />}
        {activeButton !== OrderStatusButtonType.NONE ? <OrderStatusButton buttonType={activeButton} orderId={extractId(node.id)} /> : null}
      </div>
    </li>
  );
}
